import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { cancelSubscription, createRecord, createSubscription, deleteAccount, deleteRecord, generateCreative, loadRuntimeHealth, loadWorkspace, updateRecord, uploadAttachment as uploadAttachmentFile, verifySubscriptionPayment } from "./lib/api";
import { getUser, handleAuthCallback, login, logout, onAuthChange, requestPasswordRecovery, signup, updateUser, type User } from "./lib/auth";
import { addTestGeneration, deleteTestRecord, loadTestWorkspace, makeTestDraft, resetTestWorkspace, saveTestRecord, saveTestWorkspace } from "./lib/testWorkspace";
import {
  PLANS,
  type CampaignRecord,
  type ClientRecord,
  type ContentRecord,
  type InvoiceRecord,
  type MemoryRecord,
  type ModuleKey,
  type PlanCode,
  type Profile,
  type RuntimeHealth,
  type TaskRecord,
  type WorkspaceData
} from "./lib/types";

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  }
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
  onerror: (() => void) | null;
  start: () => void;
}

const modules: Array<{ key: ModuleKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "studio", label: "Studio" },
  { key: "calendar", label: "Calendar" },
  { key: "clients", label: "Clients" },
  { key: "campaigns", label: "Campaigns" },
  { key: "money", label: "Money" },
  { key: "insights", label: "Insights" },
  { key: "memory", label: "Memory" },
  { key: "billing", label: "Plan" }
];

const contentStages = ["idea", "draft", "review", "approved", "scheduled", "published_record"];
const campaignStages = ["brief_received", "scope_review", "in_production", "client_approval", "delivered", "closed"];
const invoiceStages = ["draft", "issued_record", "due", "part_paid", "paid", "canceled", "disputed"];

type EditorKind = "task" | "content" | "client" | "campaign" | "invoice" | "memory" | "profile";
type EditorRecord = TaskRecord | ContentRecord | ClientRecord | CampaignRecord | InvoiceRecord | MemoryRecord | Profile;

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [health, setHealth] = useState<RuntimeHealth | null>(null);
  const [testing, setTesting] = useState(() => new URLSearchParams(window.location.search).get("test") === "1");

  useEffect(() => {
    let active = true;
    let unsubscribe: () => void = () => undefined;
    (async () => {
      try {
        const runtime = await loadRuntimeHealth();
        if (!active) return;
        setHealth(runtime);
        if (!runtime.services.account || !runtime.services.data) return;
        const callback = await handleAuthCallback();
        if (callback?.type === "recovery") setRecovering(true);
        setUser(await getUser());
        unsubscribe = onAuthChange((_event, nextUser) => {
          if (active) setUser(nextUser);
        });
      } catch {
        if (active) {
          setHealth({ status: "configuration_required", services: { account: false, data: false, ai: false, billing: false } });
        }
      } finally {
        if (active) setAuthReady(true);
      }
    })();
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  if (!authReady) return <LoadingScreen label="Opening your desk" />;
  const runtime = health || { status: "configuration_required" as const, services: { account: false, data: false, ai: false, billing: false } };
  function setTestMode(enabled: boolean) {
    const url = new URL(window.location.href);
    if (enabled) url.searchParams.set("test", "1");
    else url.searchParams.delete("test");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    setTesting(enabled);
  }
  return <>{user ? <AuthenticatedProduct user={user} health={runtime} /> : testing ? <TestProduct onExit={() => setTestMode(false)} /> : <PublicProduct health={runtime} onTest={() => setTestMode(true)} />}{recovering && <PasswordRecoveryDialog onClose={() => setRecovering(false)} />}</>;
}

function PublicProduct({ health, onTest }: { health: RuntimeHealth; onTest: () => void }) {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");
  const [launchNotice, setLaunchNotice] = useState("");
  const accountReady = health.services.account && health.services.data;

  function openAuth(mode: "signup" | "signin") {
    if (!accountReady) {
      setLaunchNotice("Account creation is temporarily unavailable while secure account services are being activated.");
      return;
    }
    setAuthMode(mode);
    setAuthOpen(true);
  }

  return (
    <div className="public-site">
      <header className="public-header">
        <a className="public-brand" href="https://avelinehouse.in" aria-label="Aveline House home">
          <img src="/aveline-house-logo.png" alt="Aveline House" />
        </a>
        <nav aria-label="Product navigation">
          <a href="#workflow">Workflow</a>
          <a href="#pricing">Pricing</a>
          <button className="text-button" type="button" onClick={() => openAuth("signin")}>Sign in</button>
          <button className="button compact" type="button" onClick={onTest}>Test product</button>
        </nav>
      </header>

      <main>
        <section className="public-hero">
          <div>
            <p className="eyebrow">Creator Campaign Desk / by Aveline House</p>
            <h1>Create the work.<br /><span>Run the business.</span></h1>
            <p className="hero-copy">The private operating desk for content, clients, campaigns, rights, proof, invoices, and creator memory.</p>
            <div className="hero-actions">
              <button className="button" type="button" onClick={onTest}>Open the test workspace</button>
              <a className="button secondary" href="#workflow">See the workflow</a>
            </div>
            <p className="microcopy">Test every workspace module now. Test data stays in this browser. Sign-in, live AI, uploads, and payments are separate launch services.</p>
            {launchNotice && <p className="form-error" role="status">{launchNotice}</p>}
          </div>
          <aside className="hero-product" aria-label="Creator workday preview">
            <div className="hero-product-head"><span>Today</span><strong>4 decisions</strong></div>
            <ProductPreviewRow label="Create" value="Hook routes ready for review" status="Review" />
            <ProductPreviewRow label="Plan" value="Six posts across the week" status="On track" />
            <ProductPreviewRow label="Campaign" value="Usage period needs an end date" status="Decide" />
            <ProductPreviewRow label="Money" value="One invoice is four days overdue" status="Follow up" />
          </aside>
        </section>

        <section className="workflow-section" id="workflow">
          <p className="eyebrow">One connected record</p>
          <h2>Where a creator actually works.</h2>
          <div className="workflow-grid">
            {[
              ["01", "Studio", "Develop ideas, hooks, captions, scripts, briefs, and repurposed routes in your saved voice."],
              ["02", "Calendar", "Plan organic content and paid deliverables together, with campaign context attached."],
              ["03", "Clients", "Keep contacts, opportunities, notes, and follow-up beside the work they affect."],
              ["04", "Campaign Passport", "Record scope, claims, rights, revisions, approvals, deadlines, and payment terms."],
              ["05", "Money", "Follow invoice readiness, due dates, balances, and creator-approved reminders."],
              ["06", "Memory", "Carry voice, audience, preferences, lessons, and confirmed campaign context into the next decision."]
            ].map(([number, title, copy]) => (
              <article key={number}><small>{number}</small><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
        </section>

        <PricingSection currentPlan={null} billingEnabled={health.services.billing} onChoose={() => openAuth("signup")} />

        <section className="trust-section">
          <h2>Creator control is the product rule.</h2>
          <div className="trust-grid">
            <p><strong>Private by account.</strong> Creator records are separated by database ownership rules.</p>
            <p><strong>Human approval.</strong> The desk prepares work. It does not publish, approve, send, or mark payment received without a person.</p>
            <p><strong>Clear billing.</strong> Plan, allowance, renewal state, and payment status remain visible inside the account.</p>
          </div>
        </section>
      </main>

      <footer className="public-footer">
        <img src="/aveline-house-logo.png" alt="Aveline House" />
        <p>Creator Campaign Desk is an Aveline House product.</p>
        <div><a href="/terms.html">Terms</a><a href="/privacy.html">Privacy</a><a href="mailto:reach@avelinehouse.in">Support</a><a href="https://avelinehouse.in">Aveline House</a></div>
      </footer>

      {authOpen && <AuthDialog mode={authMode} onMode={setAuthMode} onClose={() => setAuthOpen(false)} />}
    </div>
  );
}

function ProductPreviewRow({ label, value, status }: { label: string; value: string; status: string }) {
  return <div className="preview-row"><small>{label}</small><strong>{value}</strong><span>{status}</span></div>;
}

function AuthDialog({ mode, onMode, onClose }: { mode: "signup" | "signin"; onMode: (mode: "signup" | "signin") => void; onClose: () => void }) {
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dialogRef = useDialogFocus(onClose);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setNotice("");
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "").trim();
    const password = String(form.get("password") || "");
    setBusy(true);
    try {
      if (mode === "signup") {
        if (form.get("terms") !== "accepted") throw new Error("Accept the terms and privacy notice to create an account.");
        const displayName = String(form.get("displayName") || "").trim();
        const created = await signup(email, password, { full_name: displayName });
        if (!created.confirmedAt) setNotice("Check your email to verify the account, then return here to sign in.");
      } else {
        await login(email, password);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Account action failed.");
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    const emailInput = document.querySelector<HTMLInputElement>('#auth-email');
    const email = emailInput?.value.trim();
    if (!email) {
      setError("Enter your email first.");
      return;
    }
    try {
      await requestPasswordRecovery(email);
      setNotice("A password reset link has been sent if that account exists.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The reset request could not be completed.");
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section ref={dialogRef} className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="modal-close" type="button" onClick={onClose} aria-label="Close account dialog">Close</button>
        <p className="eyebrow">Creator Campaign Desk</p>
        <h2 id="auth-title">{mode === "signup" ? "Create your desk" : "Welcome back"}</h2>
        <p>{mode === "signup" ? "Start free. Upgrade only when the desk is earning its place in your work." : "Sign in to your private creator workspace."}</p>
        <form onSubmit={submit} className="auth-form">
          {mode === "signup" && <Field label="Your name" name="displayName" required autoComplete="name" />}
          <Field label="Email" name="email" type="email" required autoComplete="email" id="auth-email" />
          <Field label="Password" name="password" type="password" required minLength={10} autoComplete={mode === "signup" ? "new-password" : "current-password"} />
          {mode === "signup" && <label className="check-label"><input type="checkbox" name="terms" value="accepted" /> <span>I agree to the <a href="/terms.html" target="_blank" rel="noreferrer">account terms</a> and acknowledge the <a href="/privacy.html" target="_blank" rel="noreferrer">privacy notice</a>.</span></label>}
          {error && <p className="form-error" role="alert">{error}</p>}
          {notice && <p className="form-success" role="status">{notice}</p>}
          <button className="button" type="submit" disabled={busy}>{busy ? "Please wait" : mode === "signup" ? "Create free account" : "Sign in"}</button>
        </form>
        <div className="auth-switch">
          <button type="button" onClick={() => onMode(mode === "signup" ? "signin" : "signup")}>{mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}</button>
          {mode === "signin" && <button type="button" onClick={resetPassword}>Reset password</button>}
        </div>
      </section>
    </div>
  );
}

function PasswordRecoveryDialog({ onClose }: { onClose: () => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dialogRef = useDialogFocus(onClose);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const password = String(new FormData(event.currentTarget).get("password") || "");
    try {
      await updateUser({ password });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The password could not be updated.");
      setBusy(false);
    }
  }
  return <div className="modal-backdrop"><section ref={dialogRef} className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="recovery-title"><p className="eyebrow">Account recovery</p><h2 id="recovery-title">Choose a new password</h2><form className="auth-form" onSubmit={submit}><Field label="New password" name="password" type="password" minLength={10} required autoComplete="new-password" />{error && <p className="form-error" role="alert">{error}</p>}<button className="button" type="submit" disabled={busy}>{busy ? "Updating" : "Update password"}</button></form></section></div>;
}

function TestProduct({ onExit }: { onExit: () => void }) {
  const [active, setActive] = useState<ModuleKey>("today");
  const [data, setData] = useState<WorkspaceData>(() => loadTestWorkspace());
  const [notice, setNotice] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [editor, setEditor] = useState<{ kind: EditorKind; record?: EditorRecord; prefill?: Record<string, unknown> } | null>(null);

  function commit(update: (current: WorkspaceData) => WorkspaceData) {
    setData((current) => {
      const next = update(current);
      saveTestWorkspace(next);
      return next;
    });
  }

  function announce(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3200);
  }

  function toggleTask(task: TaskRecord) {
    commit((current) => saveTestRecord(current, "task", task.id, { completed: !task.completed }));
    announce(task.completed ? "Task reopened in this browser." : "Task completed in this browser.");
  }

  function remove(table: string, id: string, label: string) {
    if (!window.confirm(`Delete this ${label} from the browser test workspace?`)) return;
    commit((current) => deleteTestRecord(current, table, id));
    announce(`${label} deleted from the test workspace.`);
  }

  function saveEditor(payload: Record<string, unknown>) {
    if (!editor) return;
    const recordId = editor.record && "id" in editor.record ? editor.record.id : null;
    commit((current) => saveTestRecord(current, editor.kind, recordId, payload));
  }

  function generateLocal(input: { tool: string; prompt: string; campaignId?: string }) {
    const output = makeTestDraft(input.tool, input.prompt, data.profile.voice);
    commit((current) => addTestGeneration(current, input, output));
    return output;
  }

  function saveGeneratedDraft(payload: Record<string, unknown>) {
    commit((current) => saveTestRecord(current, "content", null, payload));
  }

  function reset() {
    if (!window.confirm("Reset all browser test data to the sample workspace?")) return;
    setData(resetTestWorkspace());
    announce("Test workspace reset.");
  }

  useEffect(() => {
    function shortcut(event: KeyboardEvent) {
      if (!event.altKey || event.metaKey || event.ctrlKey) return;
      const index = Number(event.key) - 1;
      if (index < 0 || index >= modules.length) return;
      event.preventDefault();
      setActive(modules[index].key);
      setMenuOpen(false);
    }
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);

  const currentModule = modules.find((module) => module.key === active)!;

  return (
    <div className="product-shell test-product-shell">
      <aside className={`product-sidebar ${menuOpen ? "open" : ""}`}>
        <a href="https://avelinehouse.in" className="product-logo"><img src="/aveline-house-logo.png" alt="Aveline House" /></a>
        <p className="product-name">Creator Campaign Desk</p>
        <div className="plan-label"><span>Test</span>Browser local</div>
        <nav aria-label="Test workspace modules">
          {modules.map((module, index) => (
            <button key={module.key} type="button" className={active === module.key ? "active" : ""} aria-current={active === module.key ? "page" : undefined} onClick={() => { setActive(module.key); setMenuOpen(false); }}>
              <small>{String(index + 1).padStart(2, "0")}</small><span>{module.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-account"><strong>{data.profile.display_name}</strong><span>Data stays in this browser</span><button type="button" onClick={onExit}>Exit test workspace</button></div>
      </aside>
      {menuOpen && <button className="sidebar-scrim" type="button" onClick={() => setMenuOpen(false)} aria-label="Close navigation" />}

      <div className="product-main">
        <header className="product-topbar">
          <button className="menu-trigger" type="button" onClick={() => setMenuOpen(true)} aria-expanded={menuOpen}>Menu</button>
          <div><small>Browser-local test workspace</small><strong>{currentModule.label}</strong></div>
          <button className="top-plan" type="button" onClick={() => setActive("billing")}>View plans</button>
        </header>
        <div className="test-mode-banner" role="status">
          <div><strong>Test workspace</strong><span>No account required. Changes persist only in this browser. Live AI, uploads, external publishing, email, and payments are not connected here.</span></div>
          <div><button type="button" onClick={reset}>Reset data</button><button type="button" onClick={onExit}>Exit test</button></div>
        </div>
        <main className="module-view" id="workspace-main">
          {active === "today" && <TodayView data={data} onAdd={() => setEditor({ kind: "task" })} onEdit={(record) => setEditor({ kind: "task", record })} onToggle={toggleTask} onNavigate={setActive} />}
          {active === "studio" && <StudioView data={data} aiEnabled={true} testMode onTestGenerate={generateLocal} onTestSave={saveGeneratedDraft} onSaved={() => announce("Draft saved in this browser.")} onRefresh={() => undefined} onUpgrade={() => setActive("billing")} />}
          {active === "calendar" && <CalendarView data={data} onAdd={(date) => setEditor({ kind: "content", prefill: { planned_at: `${date}T09:00` } })} onEdit={(record) => setEditor({ kind: "content", record })} />}
          {active === "clients" && <ClientsView data={data} onAdd={() => setEditor({ kind: "client" })} onEdit={(record) => setEditor({ kind: "client", record })} onDelete={(id) => remove("clients", id, "client")} />}
          {active === "campaigns" && <CampaignsView data={data} onAdd={() => setEditor({ kind: "campaign" })} onEdit={(record) => setEditor({ kind: "campaign", record })} onDelete={(id) => remove("campaigns", id, "campaign")} />}
          {active === "money" && <MoneyView data={data} onAdd={() => setEditor({ kind: "invoice" })} onEdit={(record) => setEditor({ kind: "invoice", record })} onDelete={(id) => remove("invoices", id, "invoice")} />}
          {active === "insights" && <InsightsView data={data} />}
          {active === "memory" && <MemoryView data={data} onAdd={() => setEditor({ kind: "memory" })} onEdit={(record) => setEditor({ kind: "memory", record })} onDelete={(id) => remove("memory_notes", id, "memory note")} onProfile={() => setEditor({ kind: "profile", record: data.profile })} />}
          {active === "billing" && <TestPlanView />}
        </main>
      </div>

      {editor && <EntityEditor data={data} kind={editor.kind} record={editor.record} prefill={editor.prefill} onClose={() => setEditor(null)} onLocalSave={saveEditor} onSaved={() => { setEditor(null); announce("Saved in this browser."); }} />}
      <div className={`app-toast ${notice ? "show" : ""}`} role="status" aria-live="polite">{notice}</div>
    </div>
  );
}

function TestPlanView() {
  return <><ModuleHeader eyebrow="Plans and billing" title="Explore the plan structure." copy="Checkout is intentionally disabled in the no-account test workspace. No payment method is requested or charged." /><section className="current-plan"><div><small>Test workspace</small><h2>Start</h2><p>Browser-local product testing. This is not an active subscription.</p></div><UsageBar used={2} plan="free" /></section><PricingSection currentPlan="free" billingEnabled={false} onChoose={() => undefined} /><p className="billing-note">Create an authenticated account only after account services open. Paid checkout requires a verified account and an active merchant configuration.</p></>;
}

function AuthenticatedProduct({ user, health }: { user: User; health: RuntimeHealth }) {
  const [active, setActive] = useState<ModuleKey>("today");
  const [data, setData] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [editor, setEditor] = useState<{ kind: EditorKind; record?: EditorRecord; prefill?: Record<string, unknown> } | null>(null);

  async function refresh() {
    setError("");
    try {
      setData(await loadWorkspace());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The workspace could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [user.id]);

  function announce(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3200);
  }

  async function toggleTask(task: TaskRecord) {
    try {
      await updateRecord("tasks", task.id, { completed: !task.completed });
      announce(task.completed ? "Task reopened." : "Task completed.");
      refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The task could not be updated.");
    }
  }

  async function remove(table: string, id: string, label: string) {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      await deleteRecord(table, id);
      announce(`${label} deleted.`);
      refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `The ${label} could not be deleted.`);
    }
  }

  if (loading) return <LoadingScreen label="Loading your creator records" />;
  if (error && !data) return <FatalState error={error} onRetry={refresh} />;
  if (!data) return null;
  if (!data.profile.onboarding_completed) return <Onboarding user={user} profile={data.profile} onComplete={refresh} />;

  const currentModule = modules.find((module) => module.key === active)!;

  return (
    <div className="product-shell">
      <aside className={`product-sidebar ${menuOpen ? "open" : ""}`}>
        <a href="https://avelinehouse.in" className="product-logo"><img src="/aveline-house-logo.png" alt="Aveline House" /></a>
        <p className="product-name">Creator Campaign Desk</p>
        <div className="plan-label"><span>{data.subscription.plan}</span>{data.subscription.status}</div>
        <nav aria-label="Workspace modules">
          {modules.map((module, index) => (
            <button key={module.key} type="button" className={active === module.key ? "active" : ""} aria-current={active === module.key ? "page" : undefined} onClick={() => { setActive(module.key); setMenuOpen(false); }}>
              <small>{String(index + 1).padStart(2, "0")}</small><span>{module.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-account"><strong>{data.profile.display_name || user.email}</strong><span>{user.email}</span><button type="button" onClick={() => logout()}>Sign out</button></div>
      </aside>
      {menuOpen && <button className="sidebar-scrim" type="button" onClick={() => setMenuOpen(false)} aria-label="Close navigation" />}

      <div className="product-main">
        <header className="product-topbar">
          <button className="menu-trigger" type="button" onClick={() => setMenuOpen(true)} aria-expanded={menuOpen}>Menu</button>
          <div><small>Creator Campaign Desk</small><strong>{currentModule.label}</strong></div>
          <button className="top-plan" type="button" onClick={() => setActive("billing")}>{data.subscription.plan === "free" ? "Upgrade" : `${data.subscription.plan} plan`}</button>
        </header>
        <main className="module-view" id="workspace-main">
          {error && <div className="inline-error" role="alert">{error}<button type="button" onClick={() => setError("")}>Dismiss</button></div>}
          {active === "today" && <TodayView data={data} onAdd={() => setEditor({ kind: "task" })} onEdit={(record) => setEditor({ kind: "task", record })} onToggle={toggleTask} onNavigate={setActive} />}
          {active === "studio" && <StudioView data={data} aiEnabled={health.services.ai} onSaved={() => { announce("Draft saved into Studio."); refresh(); }} onRefresh={refresh} onUpgrade={() => setActive("billing")} />}
          {active === "calendar" && <CalendarView data={data} onAdd={(date) => setEditor({ kind: "content", prefill: { planned_at: `${date}T09:00` } })} onEdit={(record) => setEditor({ kind: "content", record })} />}
          {active === "clients" && <ClientsView data={data} onAdd={() => setEditor({ kind: "client" })} onEdit={(record) => setEditor({ kind: "client", record })} onDelete={(id) => remove("clients", id, "client")} />}
          {active === "campaigns" && <CampaignsView data={data} onAdd={() => { if (data.subscription.plan === "free" && data.campaigns.filter((campaign) => campaign.status !== "closed").length >= 1) { announce("The free plan includes one active campaign. Upgrade to add another."); setActive("billing"); } else setEditor({ kind: "campaign" }); }} onEdit={(record) => setEditor({ kind: "campaign", record })} onDelete={(id) => remove("campaigns", id, "campaign")} />}
          {active === "money" && <MoneyView data={data} onAdd={() => setEditor({ kind: "invoice" })} onEdit={(record) => setEditor({ kind: "invoice", record })} onDelete={(id) => remove("invoices", id, "invoice")} />}
          {active === "insights" && <InsightsView data={data} />}
          {active === "memory" && <MemoryView data={data} onAdd={() => setEditor({ kind: "memory" })} onEdit={(record) => setEditor({ kind: "memory", record })} onDelete={(id) => remove("memory_notes", id, "memory note")} onProfile={() => setEditor({ kind: "profile", record: data.profile })} />}
          {active === "billing" && <BillingView data={data} user={user} billingEnabled={health.services.billing} onRefresh={refresh} announce={announce} setError={setError} />}
        </main>
      </div>

      {editor && <EntityEditor data={data} kind={editor.kind} record={editor.record} prefill={editor.prefill} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); announce("Saved securely."); refresh(); }} />}
      <div className={`app-toast ${notice ? "show" : ""}`} role="status" aria-live="polite">{notice}</div>
    </div>
  );
}

function ModuleHeader({ eyebrow, title, copy, action }: { eyebrow: string; title: string; copy: string; action?: ReactNode }) {
  return <header className="module-header"><div><p>{eyebrow}</p><h1>{title}</h1><span>{copy}</span></div>{action}</header>;
}

function TodayView({ data, onAdd, onEdit, onToggle, onNavigate }: { data: WorkspaceData; onAdd: () => void; onEdit: (task: TaskRecord) => void; onToggle: (task: TaskRecord) => void; onNavigate: (module: ModuleKey) => void }) {
  const openTasks = data.tasks.filter((task) => !task.completed);
  const due = data.invoices.filter((invoice) => invoice.status === "due").reduce((sum, invoice) => sum + invoice.amount_minor - invoice.received_minor, 0);
  return <>
    <ModuleHeader eyebrow="Your workday" title="What needs you today." copy="One decision queue across content, clients, campaigns, and money." action={<button className="button" type="button" onClick={onAdd}>Add task</button>} />
    <div className="metric-grid">
      <Metric label="Open decisions" value={String(openTasks.length)} detail="Creator actions" />
      <Metric label="Content in motion" value={String(data.content.filter((item) => !["idea", "published_record"].includes(item.status)).length)} detail="Draft through scheduled" />
      <Metric label="Active campaigns" value={String(data.campaigns.filter((campaign) => campaign.status !== "closed").length)} detail="Campaign Passports" />
      <Metric label="Payment due" value={formatMoney(due)} detail="Creator-recorded balances" />
    </div>
    <div className="two-column">
      <Panel title="Decision queue" action={<button type="button" onClick={onAdd}>Add</button>}>
        {data.tasks.length ? <ul className="record-list">{data.tasks.map((task) => <li key={task.id}><input type="checkbox" checked={task.completed} onChange={() => onToggle(task)} aria-label={`Mark ${task.title} complete`} /><div><strong className={task.completed ? "completed" : ""}>{task.title}</strong><span>{task.context || "Creator task"} / {formatDate(task.due_date)}</span></div><button type="button" onClick={() => onEdit(task)}>Edit</button></li>)}</ul> : <Empty label="No tasks yet" />}
      </Panel>
      <div>
        <Panel title="Campaign attention" action={<button type="button" onClick={() => onNavigate("campaigns")}>Open</button>}>
          {data.campaigns.filter((campaign) => campaign.status !== "closed").slice(0, 3).map((campaign) => <div className="attention-row" key={campaign.id}><small>{campaign.status.replaceAll("_", " ")}</small><strong>{campaign.name}</strong><p>{campaign.attention || "No open attention note."}</p></div>)}
        </Panel>
        <Panel title="Plan usage" action={<button type="button" onClick={() => onNavigate("billing")}>Manage</button>}>
          <UsageBar used={data.subscription.ai_generations_used} plan={data.subscription.plan} />
        </Panel>
      </div>
    </div>
  </>;
}

function StudioView({ data, aiEnabled, testMode = false, onTestGenerate, onTestSave, onSaved, onRefresh, onUpgrade }: { data: WorkspaceData; aiEnabled: boolean; testMode?: boolean; onTestGenerate?: (input: { tool: string; prompt: string; campaignId?: string }) => string; onTestSave?: (payload: Record<string, unknown>) => void; onSaved: () => void; onRefresh: () => void; onUpgrade: () => void }) {
  const [tool, setTool] = useState("ideas");
  const [prompt, setPrompt] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [attachmentId, setAttachmentId] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function generate() {
    if (testMode && onTestGenerate) {
      setError("");
      setOutput(onTestGenerate({ tool, prompt, campaignId: campaignId || undefined }));
      return;
    }
    if (!aiEnabled) {
      setError("AI generation is temporarily unavailable while the creative service is being configured.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const result = await generateCreative({ tool, prompt, campaignId: campaignId || undefined, attachmentId: attachmentId || undefined });
      setOutput(result.output);
      onRefresh();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Generation failed.";
      setError(message);
      if (message.toLowerCase().includes("allowance") || message.toLowerCase().includes("plan")) onUpgrade();
    } finally {
      setBusy(false);
    }
  }

  async function saveDraft() {
    if (!output.trim()) return;
    try {
      const payload = {
        campaign_id: campaignId || null,
        title: prompt.slice(0, 120) || "AI-assisted draft",
        platform: data.profile.platforms[0] || "Instagram",
        format: tool === "script" ? "Reel" : "Post",
        status: "draft",
        body: output,
        source: testMode ? `browser_test_${tool}` : `ai_${tool}`,
      };
      if (testMode && onTestSave) onTestSave(payload);
      else await createRecord("content_items", payload);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The draft could not be saved.");
    }
  }

  async function uploadAttachment(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    if (testMode) { setError("Uploads are not connected in the browser test workspace."); return; }
    if (file.size > 4 * 1024 * 1024) { setError("Files must be 4 MB or smaller."); return; }
    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) { setError("Upload a PDF, JPG, PNG, or WebP file."); return; }
    try {
      const attachment = await uploadAttachmentFile(file, campaignId || undefined);
      setAttachmentId(attachment.id);
      await onRefresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The file could not be uploaded.");
    }
  }

  function dictate() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) { setError("Voice input is not supported by this browser."); return; }
    const recognition = new Recognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => setPrompt((current) => `${current}${current ? " " : ""}${event.results[0][0].transcript}`);
    recognition.onerror = () => setError("Voice input could not be captured. Check microphone permission and try again.");
    recognition.start();
  }

  return <>
    <ModuleHeader eyebrow="Creative workspace" title="Make it in your voice." copy="Use creator memory and confirmed campaign context to generate editable work." />
    <section className="ai-workbench">
      <div className="ai-head"><div><small>{testMode ? "Browser-local draft simulator" : "Server-side creative assistant"}</small><h2>Draft, then decide.</h2></div><UsageBar used={data.subscription.ai_generations_used} plan={data.subscription.plan} /></div>
      <div className="ai-fields">
        <label>Tool<select value={tool} onChange={(event) => setTool(event.target.value)}><option value="ideas">Content ideas</option><option value="hooks">Hook generator</option><option value="captions">Caption generator</option><option value="script">Script generator</option><option value="brief">Brief-grounded routes</option><option value="repurpose">Repurpose package</option><option value="trend_angle">Trend-angle hypotheses</option></select></label>
        <label>Campaign context<select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}><option value="">Organic content</option>{data.campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}</select></label>
        <label>Source file<select value={attachmentId} onChange={(event) => setAttachmentId(event.target.value)}><option value="">No source file</option>{data.attachments.map((attachment) => <option key={attachment.id} value={attachment.id}>{attachment.filename}</option>)}</select></label>
        <label>Upload source<input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={uploadAttachment} disabled={testMode} aria-describedby={testMode ? "test-upload-note" : undefined} /></label>
        <label className="wide">What are you making?<textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Describe the thought, audience tension, product truth, or source content..." /></label>
        <div className="ai-actions"><button className="button secondary" type="button" onClick={dictate}>Use voice</button><button className="button" type="button" onClick={generate} disabled={!aiEnabled || busy || prompt.trim().length < 3}>{!aiEnabled ? "AI unavailable" : busy ? "Generating" : testMode ? "Generate test draft" : "Generate draft"}</button></div>
      </div>
      {testMode && <p className="test-inline-note" id="test-upload-note">Test drafts use deterministic templates in this browser. They do not call a live AI model, inspect uploaded files, or consume paid services.</p>}
      <div className="memory-strip"><span>Voice: {data.profile.voice || "Add your voice in Memory"}</span><span>Avoid: {data.profile.avoid || "No exclusions saved"}</span></div>
      {error && <p className="form-error" role="alert">{error}</p>}
      {output ? <article className="ai-output"><small>{testMode ? "Browser-local test draft / creator review required" : "AI-assisted draft / creator review required"}</small><div className="generated-copy">{output}</div><div><button className="button" type="button" onClick={saveDraft}>Save to Studio</button><button className="button secondary" type="button" onClick={() => navigator.clipboard.writeText(output)}>Copy</button></div></article> : <div className="ai-empty"><strong>Your generated draft will appear here.</strong><p>Nothing is sent, published, or approved by generating it.</p></div>}
    </section>
    <h2 className="section-title">Content pipeline</h2>
    <div className="record-grid">{data.content.slice(0, 12).map((item) => <RecordCard key={item.id} eyebrow={`${item.platform} / ${item.format}`} status={item.status} title={item.title}><p>{item.body.slice(0, 180) || "No body recorded."}</p></RecordCard>)}</div>
    <h2 className="section-title">Recent generation history</h2>
    <Panel title={testMode ? "Browser-local test history" : "Private account history"}>{data.generations.length ? <ul className="generation-list">{data.generations.map((generation) => <li key={generation.id}><small>{generation.tool.replaceAll("_", " ")} / {formatDate(generation.created_at)}</small><strong>{generation.prompt}</strong><p>{generation.output.slice(0, 240)}{generation.output.length > 240 ? "..." : ""}</p></li>)}</ul> : <Empty label="No generation history yet" />}</Panel>
  </>;
}

function CalendarView({ data, onAdd, onEdit }: { data: WorkspaceData; onAdd: (date: string) => void; onEdit: (record: ContentRecord) => void }) {
  const start = startOfWeek(new Date());
  const days = Array.from({ length: 7 }, (_, index) => { const date = new Date(start); date.setDate(start.getDate() + index); return date; });
  return <>
    <ModuleHeader eyebrow="Publishing plan" title="Organic and paid, together." copy="This calendar plans work. Publishing still requires a connected channel and explicit confirmation." />
    <div className="week-board">{days.map((day) => {
      const key = isoDate(day);
      const items = data.content.filter((item) => item.planned_at?.slice(0, 10) === key);
      const deadlines = data.campaigns.filter((campaign) => campaign.due_date === key);
      return <section key={key}><header><span>{day.toLocaleDateString("en-IN", { weekday: "short" })}</span><strong>{day.getDate()}</strong></header>{items.map((item) => <button className="calendar-card" type="button" key={item.id} onClick={() => onEdit(item)}><small>{item.platform} / {item.status}</small><strong>{item.title}</strong></button>)}{deadlines.map((campaign) => <div className="calendar-card campaign" key={campaign.id}><small>Campaign due</small><strong>{campaign.name}</strong></div>)}<button className="add-day" type="button" onClick={() => onAdd(key)}>Add here</button></section>;
    })}</div>
  </>;
}

function ClientsView({ data, onAdd, onEdit, onDelete }: { data: WorkspaceData; onAdd: () => void; onEdit: (record: ClientRecord) => void; onDelete: (id: string) => void }) {
  return <><ModuleHeader eyebrow="Private CRM" title="Keep the relationship context." copy="Contacts and notes remain private. The desk does not send messages without a connected route and confirmation." action={<button className="button" type="button" onClick={onAdd}>Add client</button>} /><div className="record-grid">{data.clients.map((client) => <RecordCard key={client.id} eyebrow={client.relationship_stage} title={client.name} status={`${data.campaigns.filter((campaign) => campaign.client_id === client.id).length} campaigns`} actions={<><button type="button" onClick={() => onEdit(client)}>Edit</button><button type="button" onClick={() => onDelete(client.id)}>Delete</button></>}><p>{client.contact_name}{client.contact_email ? ` / ${client.contact_email}` : ""}</p><p>{client.notes || "No relationship notes yet."}</p></RecordCard>)}</div></>;
}

function CampaignsView({ data, onAdd, onEdit, onDelete }: { data: WorkspaceData; onAdd: () => void; onEdit: (record: CampaignRecord) => void; onDelete: (id: string) => void }) {
  return <><ModuleHeader eyebrow="Campaign Passport" title="Know what was agreed." copy="Scope, claims, rights, revisions, approvals, deadlines, and payment terms stay attached to the work." action={<button className="button" type="button" onClick={onAdd}>New campaign</button>} /><div className="record-grid">{data.campaigns.map((campaign) => <RecordCard key={campaign.id} eyebrow={clientName(data, campaign.client_id)} status={campaign.status} title={campaign.name} actions={<><button type="button" onClick={() => onEdit(campaign)}>Edit Passport</button><button type="button" onClick={() => onDelete(campaign.id)}>Delete</button></>}><dl><div><dt>Deliverables</dt><dd>{campaign.deliverables || "Not confirmed"}</dd></div><div><dt>Rights</dt><dd>{campaign.usage_rights || "Unknown"}</dd></div><div><dt>Fee</dt><dd>{formatMoney(campaign.fee_minor, campaign.currency)}</dd></div><div><dt>Due</dt><dd>{formatDate(campaign.due_date)}</dd></div></dl>{campaign.attention && <p className="attention-copy">Needs attention: {campaign.attention}</p>}</RecordCard>)}</div></>;
}

function MoneyView({ data, onAdd, onEdit, onDelete }: { data: WorkspaceData; onAdd: () => void; onEdit: (record: InvoiceRecord) => void; onDelete: (id: string) => void }) {
  const open = data.invoices.filter((invoice) => !["paid", "canceled"].includes(invoice.status)).reduce((sum, invoice) => sum + invoice.amount_minor - invoice.received_minor, 0);
  const paid = data.invoices.reduce((sum, invoice) => sum + invoice.received_minor, 0);
  return <><ModuleHeader eyebrow="Money record" title="Follow the money beside the work." copy="Invoice and payment states are creator-recorded until a verified accounting or payment connection is added." action={<button className="button" type="button" onClick={onAdd}>Add invoice</button>} /><div className="metric-grid"><Metric label="Outstanding" value={formatMoney(open)} detail="Across local invoice records" /><Metric label="Received" value={formatMoney(paid)} detail="Recorded by the creator" /><Metric label="Due now" value={String(data.invoices.filter((invoice) => invoice.status === "due").length)} detail="Needs a decision" /><Metric label="Total records" value={String(data.invoices.length)} detail="All invoice states" /></div><div className="record-grid">{data.invoices.map((invoice) => <RecordCard key={invoice.id} eyebrow={invoice.reference} status={invoice.status} title={formatMoney(invoice.amount_minor, invoice.currency)} actions={<><button type="button" onClick={() => onEdit(invoice)}>Edit</button><button type="button" onClick={() => onDelete(invoice.id)}>Delete</button></>}><p>{clientName(data, invoice.client_id)} / due {formatDate(invoice.due_date)}</p><p>{invoice.notes || "No private note."}</p></RecordCard>)}</div></>;
}

function InsightsView({ data }: { data: WorkspaceData }) {
  const stageCounts = countBy(data.content, "status");
  const platformCounts = countBy(data.content, "platform");
  const campaignValue = data.campaigns.reduce((sum, campaign) => sum + campaign.fee_minor, 0);
  return <><ModuleHeader eyebrow="Record-based insights" title="Learn from the work you can trace." copy="These insights use your saved records. They are not live social analytics unless a verified source is connected." /><div className="insight-grid"><InsightBars title="Content pipeline" values={stageCounts} /><InsightBars title="Channel mix" values={platformCounts} /><section className="insight-card"><small>Commercial view</small><h2>{formatMoney(campaignValue)}</h2><p>Value recorded across Campaign Passports. This is not recognised revenue.</p></section><section className="insight-card black"><small>Working signal</small><h2>{data.profile.voice || "Set your creator voice"}</h2><p>Memory is applied to future generations only when the creator keeps it confirmed.</p></section></div></>;
}

function MemoryView({ data, onAdd, onEdit, onDelete, onProfile }: { data: WorkspaceData; onAdd: () => void; onEdit: (record: MemoryRecord) => void; onDelete: (id: string) => void; onProfile: () => void }) {
  return <><ModuleHeader eyebrow="Creator-owned memory" title="Carry the useful context forward." copy="You control what is saved, confirmed, excluded, corrected, or removed." action={<button className="button" type="button" onClick={onAdd}>Add memory</button>} /><div className="memory-layout"><section className="profile-card"><h2>{data.profile.display_name}</h2><p>{data.profile.niche}</p><ProfileFact label="Audience" value={data.profile.audience} /><ProfileFact label="Voice" value={data.profile.voice} /><ProfileFact label="Goals" value={data.profile.goals} /><ProfileFact label="Avoid" value={data.profile.avoid} /><ProfileFact label="Platforms" value={data.profile.platforms.join(", ")} /><button className="button light" type="button" onClick={onProfile}>Edit creator profile</button></section><Panel title="Confirmed memory" action={<button type="button" onClick={onAdd}>Add</button>}>{data.memory.length ? <ul className="memory-list">{data.memory.map((memory) => <li key={memory.id}><small>{memory.kind.replaceAll("_", " ")} / {memory.source.replaceAll("_", " ")}</small><strong>{memory.title}</strong><p>{memory.body}</p><div><button type="button" onClick={() => onEdit(memory)}>Edit</button><button type="button" onClick={() => onDelete(memory.id)}>Delete</button></div></li>)}</ul> : <Empty label="No confirmed memory yet" />}</Panel></div></>;
}

function BillingView({ data, user, billingEnabled, onRefresh, announce, setError }: { data: WorkspaceData; user: User; billingEnabled: boolean; onRefresh: () => void; announce: (message: string) => void; setError: (message: string) => void }) {
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");
  const [busyPlan, setBusyPlan] = useState<PlanCode | null>(null);
  const [accountBusy, setAccountBusy] = useState(false);

  async function choose(plan: PlanCode) {
    if (plan === "free" || plan === data.subscription.plan) return;
    if (!billingEnabled) {
      setError("Paid checkout is not open yet. Your free workspace remains available.");
      return;
    }
    setBusyPlan(plan);
    try {
      await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      const checkout = await createSubscription(plan as Exclude<PlanCode, "free">, interval);
      if (!window.Razorpay) throw new Error("Payment checkout did not load.");
      const payment = new window.Razorpay({
        key: checkout.keyId,
        subscription_id: checkout.subscriptionId,
        name: "Creator Campaign Desk",
        description: `${checkout.planName} plan / ${interval}`,
        prefill: { email: checkout.userEmail || user.email || "" },
        theme: { color: "#090909" },
        handler: async (response: Record<string, string>) => {
          try {
            await verifySubscriptionPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature
            });
            announce("Payment verified. Your plan is active.");
            await onRefresh();
          } catch (caught) {
            setError(caught instanceof Error ? caught.message : "Payment verification failed.");
          }
        },
        modal: { ondismiss: () => setBusyPlan(null) }
      });
      payment.open();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Checkout could not start.");
    } finally {
      setBusyPlan(null);
    }
  }

  async function cancelPaidPlan() {
    const confirmed = window.confirm("Cancel renewal at the end of the current billing period? Your paid access remains available until then.");
    if (!confirmed) return;
    setAccountBusy(true);
    try {
      await cancelSubscription();
      announce("Renewal canceled. Paid access remains until the current period ends.");
      await onRefresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The subscription could not be canceled.");
    } finally {
      setAccountBusy(false);
    }
  }

  async function removeAccount() {
    const phrase = `DELETE ${user.email || user.id}`;
    const confirmation = window.prompt(`This permanently deletes the workspace and stored files. Any active paid subscription is canceled immediately. Type exactly: ${phrase}`);
    if (confirmation === null) return;
    setAccountBusy(true);
    try {
      await deleteAccount(confirmation);
      await logout();
      window.location.assign("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The account could not be deleted.");
      setAccountBusy(false);
    }
  }

  return <><ModuleHeader eyebrow="Plan and billing" title="Know exactly what you are paying for." copy={billingEnabled ? "Paid plans renew automatically until canceled. Payment details are handled by the checkout provider, not stored in the Desk." : "Your free workspace is active. Paid checkout remains closed until merchant verification and payment testing are complete."} /><section className="current-plan"><div><small>Current plan</small><h2>{data.subscription.plan}</h2><p>Status: {data.subscription.status}{data.subscription.current_period_end ? ` / current period ends ${formatDate(data.subscription.current_period_end)}` : ""}{data.subscription.cancel_at_period_end ? " / renewal canceled" : ""}</p></div><UsageBar used={data.subscription.ai_generations_used} plan={data.subscription.plan} /></section><div className="billing-toggle"><button type="button" className={interval === "monthly" ? "active" : ""} onClick={() => setInterval("monthly")}>Monthly</button><button type="button" className={interval === "annual" ? "active" : ""} onClick={() => setInterval("annual")}>Annual / save about 17%</button></div><PricingSection currentPlan={data.subscription.plan} interval={interval} billingEnabled={billingEnabled} onChoose={choose} busyPlan={busyPlan} /><section className="account-controls"><div><h2>Account controls</h2><p>Cancel renewal without losing the current paid period, or permanently delete the account and its stored files.</p></div><div>{data.subscription.plan !== "free" && !data.subscription.cancel_at_period_end && <button className="button secondary" type="button" disabled={accountBusy} onClick={cancelPaidPlan}>Cancel renewal</button>}<button className="danger-button" type="button" disabled={accountBusy} onClick={removeAccount}>Delete account</button></div></section><p className="billing-note">Taxes may apply. Refund eligibility depends on the published terms and applicable law. Account and billing copy requires legal and merchant review before public launch.</p></>;
}

function PricingSection({ currentPlan, interval = "monthly", billingEnabled = true, onChoose, busyPlan = null }: { currentPlan: PlanCode | null; interval?: "monthly" | "annual"; billingEnabled?: boolean; onChoose: (plan: PlanCode) => void; busyPlan?: PlanCode | null }) {
  return <section className="pricing-section" id="pricing"><div className="pricing-heading"><p className="eyebrow">Plans / prices in USD</p><h2>Start free. Pay when the workflow becomes commercial.</h2></div><div className="pricing-grid">{PLANS.map((plan) => {
    const amount = interval === "monthly" ? plan.monthly : plan.annual;
    const checkoutUnavailable = plan.code !== "free" && !billingEnabled;
    return <article key={plan.code} className={plan.recommended ? "recommended" : ""}><div><small>{plan.recommended ? "Recommended" : "Plan"}</small><h3>{plan.name}</h3><p>{plan.description}</p></div><div className="price"><strong>{amount === 0 ? "Free" : `$${amount.toLocaleString("en-US")}`}</strong>{amount > 0 && <span>/{interval === "monthly" ? "month" : "year"}</span>}</div><ul>{plan.features.map((feature) => <li key={feature}>{feature}</li>)}</ul><button className="button" type="button" disabled={checkoutUnavailable || currentPlan === plan.code || busyPlan === plan.code} onClick={() => onChoose(plan.code)}>{checkoutUnavailable ? "Checkout not open" : currentPlan === plan.code ? "Current plan" : busyPlan === plan.code ? "Opening checkout" : plan.code === "free" ? "Start free" : `Choose ${plan.name}`}</button></article>;
  })}</div></section>;
}

function Onboarding({ user, profile, onComplete }: { user: User; profile: WorkspaceData["profile"]; onComplete: () => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await updateRecord("profiles", null, {
        display_name: String(form.get("display_name") || ""),
        brand_name: String(form.get("brand_name") || ""),
        niche: String(form.get("niche") || ""),
        audience: String(form.get("audience") || ""),
        voice: String(form.get("voice") || ""),
        goals: String(form.get("goals") || ""),
        avoid: String(form.get("avoid") || ""),
        platforms: String(form.get("platforms") || "").split(",").map((item) => item.trim()).filter(Boolean),
        onboarding_completed: true,
      });
      onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The creator profile could not be saved.");
    }
    setBusy(false);
  }
  return <div className="onboarding"><div className="onboarding-brand"><img src="/aveline-house-logo.png" alt="Aveline House" /><p>Creator Campaign Desk</p></div><form onSubmit={submit}><p className="eyebrow">Set up your memory</p><h1>Make the Desk sound like you.</h1><p>These details become visible context for future creative work. You can change or remove them later.</p><div className="form-grid"><Field label="Creator name" name="display_name" defaultValue={profile.display_name} required /><Field label="Creator brand, optional" name="brand_name" defaultValue={profile.brand_name} /><Field label="Niche" name="niche" placeholder="Lifestyle, food, finance..." required /><Field label="Platforms" name="platforms" placeholder="Instagram, YouTube" required /><Field label="Audience" name="audience" textarea required /><Field label="Voice" name="voice" textarea placeholder="Specific, warm, funny, direct..." required /><Field label="Goals" name="goals" textarea required /><Field label="Avoid" name="avoid" textarea placeholder="Phrases, claims, tones, topics..." /></div>{error && <p className="form-error">{error}</p>}<button className="button" type="submit" disabled={busy}>{busy ? "Saving" : "Open my desk"}</button></form></div>;
}

function EntityEditor({ data, kind, record, prefill, onClose, onSaved, onLocalSave }: { data: WorkspaceData; kind: EditorKind; record?: EditorRecord; prefill?: Record<string, unknown>; onClose: () => void; onSaved: () => void; onLocalSave?: (payload: Record<string, unknown>) => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dialogRef = useDialogFocus(onClose);
  const initial = { ...(record || {}), ...(prefill || {}) } as Record<string, unknown>;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const payload = editorPayload(kind, form);
    const table = { task: "tasks", content: "content_items", client: "clients", campaign: "campaigns", invoice: "invoices", memory: "memory_notes", profile: "profiles" }[kind];
    try {
      if (onLocalSave) onLocalSave(payload);
      else if (record || kind === "profile") await updateRecord(table, record && "id" in record ? record.id : null, payload);
      else await createRecord(table, payload);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The record could not be saved.");
      setBusy(false);
    }
  }

  return <div className="modal-backdrop"><section ref={dialogRef} className="entity-dialog" role="dialog" aria-modal="true" aria-labelledby="editor-title"><header><div><small>{record ? "Edit" : "Add"}</small><h2 id="editor-title">{kind.replaceAll("_", " ")}</h2></div><button type="button" onClick={onClose}>Close</button></header><form onSubmit={submit}><div className="form-grid">{editorFields(kind, initial, data)}</div>{error && <p className="form-error">{error}</p>}<div className="dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button" type="submit" disabled={busy}>{busy ? "Saving" : "Save"}</button></div></form></section></div>;
}

function editorFields(kind: EditorKind, value: Record<string, unknown>, data: WorkspaceData) {
  if (kind === "profile") return <><Field label="Creator name" name="display_name" defaultValue={text(value.display_name)} required /><Field label="Creator brand" name="brand_name" defaultValue={text(value.brand_name)} /><Field label="Niche" name="niche" defaultValue={text(value.niche)} required /><Field label="Platforms" name="platforms" defaultValue={Array.isArray(value.platforms) ? value.platforms.join(", ") : text(value.platforms)} required /><Field label="Audience" name="audience" defaultValue={text(value.audience)} textarea /><Field label="Voice" name="voice" defaultValue={text(value.voice)} textarea required /><Field label="Goals" name="goals" defaultValue={text(value.goals)} textarea /><Field label="Avoid" name="avoid" defaultValue={text(value.avoid)} textarea /></>;
  if (kind === "task") return <><Field label="Task" name="title" defaultValue={text(value.title)} required /><Field label="Context" name="context" defaultValue={text(value.context)} /><Field label="Due date" name="due_date" type="date" defaultValue={text(value.due_date)} /></>;
  if (kind === "content") return <><Field label="Working title" name="title" defaultValue={text(value.title)} required /><Field label="Platform" name="platform" defaultValue={text(value.platform) || data.profile.platforms[0] || "Instagram"} required /><Field label="Format" name="format" defaultValue={text(value.format) || "Reel"} required /><SelectField label="Status" name="status" value={text(value.status) || "idea"} options={contentStages} /><Field label="Planned date and time" name="planned_at" type="datetime-local" defaultValue={toLocalDateTime(text(value.planned_at))} /><SelectField label="Campaign" name="campaign_id" value={text(value.campaign_id)} options={["", ...data.campaigns.map((campaign) => campaign.id)]} labels={{ "": "Organic content", ...Object.fromEntries(data.campaigns.map((campaign) => [campaign.id, campaign.name])) }} /><Field label="Draft or creative direction" name="body" defaultValue={text(value.body)} textarea /></>;
  if (kind === "client") return <><Field label="Client or brand" name="name" defaultValue={text(value.name)} required /><Field label="Contact name" name="contact_name" defaultValue={text(value.contact_name)} /><Field label="Contact email" name="contact_email" type="email" defaultValue={text(value.contact_email)} /><Field label="Preferred channel" name="preferred_channel" defaultValue={text(value.preferred_channel)} /><SelectField label="Relationship stage" name="relationship_stage" value={text(value.relationship_stage) || "prospect"} options={["prospect", "active", "paused", "past"]} /><Field label="Private notes" name="notes" defaultValue={text(value.notes)} textarea /></>;
  if (kind === "campaign") return <><Field label="Campaign name" name="name" defaultValue={text(value.name)} required /><SelectField label="Client" name="client_id" value={text(value.client_id)} options={["", ...data.clients.map((client) => client.id)]} labels={{ "": "No client selected", ...Object.fromEntries(data.clients.map((client) => [client.id, client.name])) }} /><SelectField label="Status" name="status" value={text(value.status) || "brief_received"} options={campaignStages} /><Field label="Fee in rupees" name="fee_rupees" type="number" min={0} defaultValue={String(Number(value.fee_minor || 0) / 100)} /><Field label="Due date" name="due_date" type="date" defaultValue={text(value.due_date)} /><Field label="Deliverables" name="deliverables" defaultValue={text(value.deliverables)} textarea required /><Field label="Required claims" name="required_claims" defaultValue={text(value.required_claims)} textarea /><Field label="Prohibited claims" name="prohibited_claims" defaultValue={text(value.prohibited_claims)} textarea /><Field label="Disclosure" name="disclosure" defaultValue={text(value.disclosure)} /><Field label="Usage rights" name="usage_rights" defaultValue={text(value.usage_rights)} textarea /><Field label="Exclusivity" name="exclusivity" defaultValue={text(value.exclusivity)} /><Field label="Included revisions" name="revision_cap" type="number" min={0} defaultValue={text(value.revision_cap)} /><Field label="Payment terms" name="payment_terms" defaultValue={text(value.payment_terms)} /><Field label="Needs attention" name="attention" defaultValue={text(value.attention)} textarea /><Field label="Pasted brief" name="brief_text" defaultValue={text(value.brief_text)} textarea /></>;
  if (kind === "invoice") return <><Field label="Reference" name="reference" defaultValue={text(value.reference) || "INV-"} required /><SelectField label="Client" name="client_id" value={text(value.client_id)} options={["", ...data.clients.map((client) => client.id)]} labels={{ "": "No client selected", ...Object.fromEntries(data.clients.map((client) => [client.id, client.name])) }} /><SelectField label="Campaign" name="campaign_id" value={text(value.campaign_id)} options={["", ...data.campaigns.map((campaign) => campaign.id)]} labels={{ "": "No campaign selected", ...Object.fromEntries(data.campaigns.map((campaign) => [campaign.id, campaign.name])) }} /><Field label="Amount in rupees" name="amount_rupees" type="number" min={0} defaultValue={String(Number(value.amount_minor || 0) / 100)} required /><Field label="Received in rupees" name="received_rupees" type="number" min={0} defaultValue={String(Number(value.received_minor || 0) / 100)} /><Field label="Due date" name="due_date" type="date" defaultValue={text(value.due_date)} /><SelectField label="Status" name="status" value={text(value.status) || "draft"} options={invoiceStages} /><Field label="Private notes" name="notes" defaultValue={text(value.notes)} textarea /></>;
  return <><SelectField label="Memory type" name="kind" value={text(value.kind) || "creative_preference"} options={["creative_preference", "audience_feedback", "performance_observation", "commercial_lesson", "workflow_note"]} /><Field label="Title" name="title" defaultValue={text(value.title)} required /><Field label="What should the Desk remember?" name="body" defaultValue={text(value.body)} textarea required /><SelectField label="Source" name="source" value={text(value.source) || "creator_entered"} options={["creator_entered", "confirmed_record", "creator_confirmed_suggestion"]} /></>;
}

function editorPayload(kind: EditorKind, form: FormData) {
  const raw = Object.fromEntries(form.entries());
  if (kind === "profile") return { display_name: raw.display_name, brand_name: raw.brand_name, niche: raw.niche, platforms: String(raw.platforms || "").split(",").map((item) => item.trim()).filter(Boolean), audience: raw.audience, voice: raw.voice, goals: raw.goals, avoid: raw.avoid };
  if (kind === "task") return { title: raw.title, context: raw.context, due_date: raw.due_date || null };
  if (kind === "content") return { title: raw.title, platform: raw.platform, format: raw.format, status: raw.status, planned_at: raw.planned_at ? new Date(String(raw.planned_at)).toISOString() : null, campaign_id: raw.campaign_id || null, body: raw.body, source: "manual" };
  if (kind === "client") return { name: raw.name, contact_name: raw.contact_name, contact_email: raw.contact_email, preferred_channel: raw.preferred_channel, relationship_stage: raw.relationship_stage, notes: raw.notes };
  if (kind === "campaign") return { client_id: raw.client_id || null, name: raw.name, status: raw.status, fee_minor: Math.round(Number(raw.fee_rupees || 0) * 100), currency: "INR", due_date: raw.due_date || null, deliverables: raw.deliverables, required_claims: raw.required_claims, prohibited_claims: raw.prohibited_claims, disclosure: raw.disclosure, usage_rights: raw.usage_rights, exclusivity: raw.exclusivity, revision_cap: raw.revision_cap === "" ? null : Number(raw.revision_cap), payment_terms: raw.payment_terms, attention: raw.attention, brief_text: raw.brief_text };
  if (kind === "invoice") return { client_id: raw.client_id || null, campaign_id: raw.campaign_id || null, reference: raw.reference, amount_minor: Math.round(Number(raw.amount_rupees || 0) * 100), received_minor: Math.round(Number(raw.received_rupees || 0) * 100), currency: "INR", due_date: raw.due_date || null, status: raw.status, notes: raw.notes };
  return { kind: raw.kind, title: raw.title, body: raw.body, source: raw.source, confirmed: true, excluded: false };
}

function Field(props: { label: string; name: string; id?: string; type?: string; defaultValue?: string; placeholder?: string; textarea?: boolean; required?: boolean; minLength?: number; min?: number; autoComplete?: string }) {
  const { label, textarea, ...inputProps } = props;
  const id = props.id || `field-${props.name}`;
  return <label className={textarea ? "wide" : ""} htmlFor={id}><span>{label}</span>{textarea ? <textarea {...inputProps} id={id} /> : <input {...inputProps} id={id} />}</label>;
}

function SelectField({ label, name, value, options, labels = {} }: { label: string; name: string; value: string; options: string[]; labels?: Record<string, string> }) {
  return <label><span>{label}</span><select name={name} defaultValue={value}>{options.map((option) => <option key={option} value={option}>{labels[option] || option.replaceAll("_", " ")}</option>)}</select></label>;
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <article className="metric"><small>{label}</small><strong>{value}</strong><span>{detail}</span></article>;
}

function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return <section className="panel"><header><h2>{title}</h2>{action}</header><div className="panel-content">{children}</div></section>;
}

function RecordCard({ eyebrow, status, title, children, actions }: { eyebrow: string; status?: string; title: string; children: ReactNode; actions?: ReactNode }) {
  return <article className="record-card"><div className="card-meta"><small>{eyebrow}</small>{status && <span>{status.replaceAll("_", " ")}</span>}</div><h2>{title}</h2><div className="record-copy">{children}</div>{actions && <div className="card-actions">{actions}</div>}</article>;
}

function ProfileFact({ label, value }: { label: string; value: string }) {
  return <div><small>{label}</small><strong>{value || "Not set"}</strong></div>;
}

function UsageBar({ used, plan }: { used: number; plan: PlanCode }) {
  const definition = PLANS.find((item) => item.code === plan)!;
  const percentage = Math.min(100, Math.round((used / definition.aiLimit) * 100));
  return <div className="usage"><div><span>AI use</span><strong>{used} / {definition.aiLimit}</strong></div><div className="usage-track"><span style={{ width: `${percentage}%` }} /></div></div>;
}

function InsightBars({ title, values }: { title: string; values: Record<string, number> }) {
  const max = Math.max(1, ...Object.values(values));
  return <section className="insight-card"><small>Record distribution</small><h2>{title}</h2>{Object.entries(values).map(([label, value]) => <div className="bar-row" key={label}><span>{label.replaceAll("_", " ")}</span><div><i style={{ width: `${(value / max) * 100}%` }} /></div><strong>{value}</strong></div>)}</section>;
}

function Empty({ label }: { label: string }) { return <div className="empty-state"><strong>{label}</strong><p>Add a record when it becomes useful.</p></div>; }
function LoadingScreen({ label }: { label: string }) { return <div className="loading-screen"><img src="/aveline-house-logo.png" alt="Aveline House" /><span>{label}</span></div>; }
function FatalState({ error, onRetry }: { error: string; onRetry: () => void }) { return <div className="fatal-state"><h1>The desk could not open.</h1><p>{error}</p><button className="button" type="button" onClick={onRetry}>Try again</button></div>; }

function useDialogFocus(onClose?: () => void) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const selector = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(selector)).filter((element) => !element.hidden);
    focusable()[0]?.focus();

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape" && onClose) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    dialog.addEventListener("keydown", handleKey);
    return () => {
      dialog.removeEventListener("keydown", handleKey);
      previous?.focus();
    };
  }, [onClose]);
  return ref;
}

function formatMoney(minor: number, currency = "INR") { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format((minor || 0) / 100); }
function formatDate(value: string | null) { if (!value) return "No date"; return new Date(value.includes("T") ? value : `${value}T12:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
function countBy<T extends object>(items: T[], key: keyof T) { return items.reduce<Record<string, number>>((acc, item) => { const value = String(item[key] || "Unknown"); acc[value] = (acc[value] || 0) + 1; return acc; }, {}); }
function clientName(data: WorkspaceData, id: string | null) { return data.clients.find((client) => client.id === id)?.name || "No client"; }
function text(value: unknown) { return value == null ? "" : String(value); }
function toLocalDateTime(value: string) { if (!value) return ""; const date = new Date(value); const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000); return local.toISOString().slice(0, 16); }
function startOfWeek(value: Date) { const date = new Date(value); const day = date.getDay(); date.setHours(12, 0, 0, 0); date.setDate(date.getDate() - ((day + 6) % 7)); return date; }
function isoDate(value: Date) { const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000); return local.toISOString().slice(0, 10); }
function loadScript(src: string) { return new Promise<void>((resolve, reject) => { if (document.querySelector(`script[src="${src}"]`)) return resolve(); const script = document.createElement("script"); script.src = src; script.async = true; script.onload = () => resolve(); script.onerror = () => reject(new Error("Secure checkout could not load.")); document.head.appendChild(script); }); }

export default App;
